import { additionalFeedCalendar } from './sports-feeds'
import type { SportKey } from './sports-schedule'
import { ENV } from './envs/constants'

// ─── The sports calendar ─────────────────────────────────────────────────────
//
// Which competitions the Odds API pipeline creates and resolves markets for,
// and when. Plain data checked into the repo: a change is a one-line PR, and
// every process (scheduler, API, admin page) reads the same list with nothing
// to seed or sync. Status is computed from the dates at run time.

export type SportId =
  | 'nfl'
  | 'cfb'
  | 'f1'
  | 'tdf'
  | 'soccer'
  | 'mlb'
  | 'nba'
  | 'wnba'
  | 'nhl'
  | 'cbb'
  | 'football'
  | 'basketball'
  | 'wcbb'
  | 'hockey'
  | 'afl'
  | 'nrl'
  | 'handball'
  | 'tennis'

export type SportsCalendarStatus = 'upcoming' | 'active' | 'completed'

export interface SportsCalendarEntry {
  sport: SportId
  /** Human-readable competition name, e.g. "NFL Regular Season 2026–27" */
  competition: string
  /** Stable slug, e.g. "nfl-regular-2026"; ties the phases of one competition together */
  competitionId: string
  /** Phase within the competition, e.g. "Regular Season", "Wild Card", "Week 1" */
  phase: string
  /** ISO date YYYY-MM-DD */
  startDate: string
  /** ISO date YYYY-MM-DD */
  endDate: string
  /**
   * The scheduler creates markets for games in this window by default. The
   * switches on /admin/sports override it without a deploy.
   */
  autoCreate: boolean
  /** Resolve markets when games finish */
  autoResolve: boolean
  /**
   * Games can end level, so the market is three-way (home, away, Draw).
   * Defaults to true for soccer and false for everything else; a false
   * default means a tie resolves the binary market at 50%.
   */
  tiesAllowed?: boolean
  notes?: string
  /**
   * Why this phase can't be switched on: it needs code first. Neither the
   * scheduler nor an admin run creates games in a locked phase.
   */
  locked?: string
  /** The Odds API sport key. Competitions without one are created by hand. */
  oddsKey?: string
  /**
   * A feed other than The Odds API that the scheduler creates this phase's
   * markets from: 'bdl-f1' is BALLDONTLIE's F1 API (shared/f1-markets.ts).
   */
  provider?: 'bdl-f1'
  /**
   * F1: how many rounds the season was announced with. The season title
   * markets need it to pay out: the provider must list at least this many
   * weekends (f1CalendarListsEveryRound).
   */
  rounds?: number
}

type CalendarInput = Omit<SportsCalendarEntry, 'oddsKey'>

// Knockout games go to extra time and penalties, and the resolver only sees
// goals, so a shootout would resolve as a Draw. Decide between the 90-minute
// result and who advances, and check what /scores reports, before unlocking.
const KNOCKOUT_LOCK =
  'Knockout games need an extra time and penalties rule before they can run'

// The Odds API sport key per competition. F1 and the Tour de France are
// deliberately absent: the provider's coverage is not enough to resolve from
// scores, so those stay manual. NWSL is not covered at all. Every key here
// had scores in the provider's sports list on 28 Sep 2026.
const ODDS_KEY_BY_COMPETITION: Record<string, string> = {
  'nfl-preseason-2026': 'americanfootball_nfl_preseason',
  'nfl-regular-2026': 'americanfootball_nfl',
  'nfl-playoffs-2027': 'americanfootball_nfl',
  'cfb-regular-2026': 'americanfootball_ncaaf',
  'cfb-cfp-2027': 'americanfootball_ncaaf',
  'mlb-2026': 'baseball_mlb',
  'mlb-2027': 'baseball_mlb',
  'nba-regular-2026-27': 'basketball_nba',
  'nba-playoffs-2027': 'basketball_nba',
  'wnba-2026': 'basketball_wnba',
  'wnba-2027': 'basketball_wnba',
  'epl-2026-27': 'soccer_epl',
  'mls-2026': 'soccer_usa_mls',
  'mls-2027': 'soccer_usa_mls',
  'ucl-2026-27': 'soccer_uefa_champs_league',
  'laliga-2026-27': 'soccer_spain_la_liga',
  'seriea-2026-27': 'soccer_italy_serie_a',
  'bundesliga-2026-27': 'soccer_germany_bundesliga',
  'ligue1-2026-27': 'soccer_france_ligue_one',
  'nhl-2026-27': 'icehockey_nhl',
  'ncaab-tournament-2027': 'basketball_ncaab',
  'ncaab-season-2026-27': 'basketball_ncaab',
}

const ENTRIES: CalendarInput[] = [
  // ── NFL ─────────────────────────────────────────────────────────────────────
  {
    sport: 'nfl',
    competition: 'NFL Preseason 2026',
    competitionId: 'nfl-preseason-2026',
    phase: 'Preseason',
    startDate: '2026-08-06',
    endDate: '2026-08-29',
    autoCreate: false,
    autoResolve: true,
    notes: 'Preseason markets are off by default; enable manually per game',
  },
  {
    sport: 'nfl',
    competition: 'NFL Regular Season 2026–27',
    competitionId: 'nfl-regular-2026',
    phase: 'Regular Season',
    startDate: '2026-09-09',
    endDate: '2027-01-10',
    autoCreate: true,
    autoResolve: true,
    notes:
      'Rolling 14-day creation window; ~272 games total. Ties resolve at 50%.',
  },
  {
    sport: 'nfl',
    competition: 'NFL Playoffs 2027',
    competitionId: 'nfl-playoffs-2027',
    phase: 'Wild Card',
    startDate: '2027-01-16',
    endDate: '2027-01-18',
    autoCreate: true,
    autoResolve: true,
  },
  {
    sport: 'nfl',
    competition: 'NFL Playoffs 2027',
    competitionId: 'nfl-playoffs-2027',
    phase: 'Divisional Round',
    startDate: '2027-01-23',
    endDate: '2027-01-24',
    autoCreate: true,
    autoResolve: true,
  },
  {
    sport: 'nfl',
    competition: 'NFL Playoffs 2027',
    competitionId: 'nfl-playoffs-2027',
    phase: 'Conference Championships',
    startDate: '2027-01-31',
    endDate: '2027-01-31',
    autoCreate: true,
    autoResolve: true,
  },
  {
    sport: 'nfl',
    competition: 'NFL Playoffs 2027',
    competitionId: 'nfl-playoffs-2027',
    phase: 'Super Bowl LXI',
    startDate: '2027-02-14',
    endDate: '2027-02-14',
    autoCreate: true,
    autoResolve: true,
  },

  // ── College Football ─────────────────────────────────────────────────────────
  {
    sport: 'cfb',
    competition: 'NCAA Football 2026',
    competitionId: 'cfb-regular-2026',
    phase: 'Regular Season',
    startDate: '2026-08-29',
    endDate: '2026-11-28',
    autoCreate: false,
    autoResolve: true,
    notes:
      'Scope should be Top 25 matchups and rivalry games only, but the pipeline has no such filter yet, so auto-create stays off; the provider returns every FBS game.',
  },
  {
    sport: 'cfb',
    competition: 'NCAA Football 2026',
    competitionId: 'cfb-regular-2026',
    phase: 'Conference Championships',
    startDate: '2026-12-05',
    endDate: '2026-12-06',
    autoCreate: true,
    autoResolve: true,
  },
  {
    sport: 'cfb',
    competition: 'NCAA Football 2026',
    competitionId: 'cfb-regular-2026',
    phase: 'Bowl Season',
    // From the first bowl (12 Dec) so the early bowls and Army–Navy, the one
    // regular-season game left that week, get markets too.
    startDate: '2026-12-12',
    endDate: '2027-01-01',
    autoCreate: true,
    autoResolve: true,
  },
  {
    sport: 'cfb',
    competition: 'College Football Playoff 2026–27',
    competitionId: 'cfb-cfp-2027',
    phase: 'CFP (12-team)',
    // First round 18–19 Dec, quarterfinals 30 Dec and 1 Jan, semifinals
    // 14–15 Jan, national championship Monday 25 Jan in Las Vegas.
    startDate: '2026-12-18',
    endDate: '2027-01-25',
    autoCreate: true,
    autoResolve: true,
    notes:
      'The NCAAF feed includes all bowls during this window; the CFP games are identifiable by team rank once the bracket is set.',
  },

  // ── F1 ──────────────────────────────────────────────────────────────────────
  {
    sport: 'f1',
    competition: 'F1 World Championship 2026',
    competitionId: 'f1-2026',
    phase: '2026 Remaining',
    startDate: '2026-08-28',
    endDate: '2026-12-07',
    // The sports-f1-create job makes race winner, podium, sprint winner and
    // pole markets from BALLDONTLIE once this is switched on in
    // /admin/sports; off until then.
    provider: 'bdl-f1',
    autoCreate: false,
    autoResolve: true,
    notes:
      'Remaining races: Zandvoort, Monza, Singapore, Austin, Mexico City, São Paulo, Las Vegas, Abu Dhabi. Race winner, podium, pole and sprint winner per weekend from BALLDONTLIE (shared/f1-markets.ts).',
  },
  {
    sport: 'f1',
    competition: 'F1 World Championship 2027',
    competitionId: 'f1-2027',
    phase: '2027 Season',
    // Announced 16 Sep 2026: 24 rounds, from Bahrain (12–14 Mar) to Abu
    // Dhabi (10–12 Dec), 10 of them sprint weekends. The last date must stay
    // the finale's race day: the title markets check the provider's
    // calendar against it (f1CalendarReachesEnd).
    startDate: '2027-03-12',
    endDate: '2027-12-12',
    rounds: 24,
    provider: 'bdl-f1',
    autoCreate: false,
    autoResolve: false,
    notes:
      'Bahrain 12–14 Mar to Abu Dhabi 10–12 Dec. Switch on in the winter to open the drivers’ and constructors’ title markets before the first weekend; session markets follow within F1_DAYS_AHEAD of each session.',
  },

  // ── Tour de France ───────────────────────────────────────────────────────────
  {
    sport: 'tdf',
    competition: 'Tour de France 2027',
    competitionId: 'tdf-2027',
    phase: 'TdF 2027',
    startDate: '2027-07-03',
    endDate: '2027-07-25',
    autoCreate: false,
    autoResolve: false,
    notes:
      'DECISION NEEDED: per-stage markets (21 stages) vs. GC winner market only. Also consider: KoM / points jersey / best young rider classifications? Dates are estimates — confirm once official route announced.',
  },

  // ── Soccer: EPL ──────────────────────────────────────────────────────────────
  {
    sport: 'soccer',
    competition: 'English Premier League 2026–27',
    competitionId: 'epl-2026-27',
    phase: 'Season',
    startDate: '2026-08-15',
    // The final round is on 30 May 2027.
    endDate: '2027-05-30',
    autoCreate: true,
    autoResolve: true,
    notes: '~380 games total; rolling 14-day creation window',
  },

  // ── Soccer: top European leagues ─────────────────────────────────────────────
  // About 40 games a weekend between them. Each window ends on the last
  // matchday, so the Bundesliga and Ligue 1 relegation play-offs (two legs,
  // extra time) stay out. Dates are approximate: the provider only lists
  // games that are scheduled, so a window only has to bracket the season.
  {
    sport: 'soccer',
    competition: 'La Liga 2026–27',
    competitionId: 'laliga-2026-27',
    phase: 'Season',
    startDate: '2026-08-14',
    endDate: '2027-05-30',
    autoCreate: false,
    autoResolve: true,
    notes: '380 games, about 10 a weekend',
  },
  {
    sport: 'soccer',
    competition: 'Serie A 2026–27',
    competitionId: 'seriea-2026-27',
    phase: 'Season',
    startDate: '2026-08-22',
    endDate: '2027-05-30',
    autoCreate: false,
    autoResolve: true,
    notes: '380 games, about 10 a weekend',
  },
  {
    sport: 'soccer',
    competition: 'Bundesliga 2026–27',
    competitionId: 'bundesliga-2026-27',
    phase: 'Season',
    startDate: '2026-08-28',
    endDate: '2027-05-15',
    autoCreate: false,
    autoResolve: true,
    notes: '306 games, about 9 a weekend',
  },
  {
    sport: 'soccer',
    competition: 'Ligue 1 2026–27',
    competitionId: 'ligue1-2026-27',
    phase: 'Season',
    startDate: '2026-08-14',
    endDate: '2027-05-16',
    autoCreate: false,
    autoResolve: true,
    notes: '306 games, about 9 a weekend',
  },

  // ── Soccer: Champions League ─────────────────────────────────────────────────
  {
    sport: 'soccer',
    competition: 'UEFA Champions League 2026–27',
    competitionId: 'ucl-2026-27',
    phase: 'League Phase',
    // Eight matchdays, the last two in January. Every game can end level and
    // is settled at 90 minutes, so it resolves like a league game.
    startDate: '2026-09-15',
    endDate: '2027-01-31',
    autoCreate: false,
    autoResolve: true,
  },
  {
    sport: 'soccer',
    competition: 'UEFA Champions League 2026–27',
    competitionId: 'ucl-2026-27',
    phase: 'Knockout Phase',
    startDate: '2027-02-11',
    endDate: '2027-05-30',
    autoCreate: false,
    autoResolve: true,
    locked: KNOCKOUT_LOCK,
  },

  // ── Soccer: MLS ──────────────────────────────────────────────────────────────
  {
    sport: 'soccer',
    competition: 'MLS 2026',
    competitionId: 'mls-2026',
    phase: 'Regular Season',
    startDate: '2026-08-01',
    endDate: '2026-10-19',
    // Off for launch: three weeks left and about 15 games a week. The launch
    // set is NFL, EPL, the MLB postseason, the WNBA playoffs and the college
    // football post-season.
    autoCreate: false,
    autoResolve: true,
    notes: 'Off for launch (low interest, high volume); season ends 19 Oct',
  },
  {
    sport: 'soccer',
    competition: 'MLS 2026',
    competitionId: 'mls-2026',
    phase: 'MLS Cup Playoffs',
    startDate: '2026-10-22',
    endDate: '2026-11-28',
    autoCreate: false,
    autoResolve: true,
    locked: KNOCKOUT_LOCK,
  },
  {
    sport: 'soccer',
    competition: 'MLS 2027',
    competitionId: 'mls-2027',
    phase: 'Season',
    startDate: '2027-02-27',
    endDate: '2027-11-27',
    autoCreate: false,
    autoResolve: false,
    notes: 'Enable auto-create once API provider confirmed',
  },

  // ── Soccer: NWSL ─────────────────────────────────────────────────────────────
  {
    sport: 'soccer',
    competition: 'NWSL 2026',
    competitionId: 'nwsl-2026',
    phase: 'Regular Season',
    startDate: '2026-08-01',
    endDate: '2026-10-11',
    // The Odds API doesn't cover the NWSL.
    autoCreate: false,
    autoResolve: true,
  },
  {
    sport: 'soccer',
    competition: 'NWSL 2026',
    competitionId: 'nwsl-2026',
    phase: 'Playoffs',
    startDate: '2026-10-16',
    endDate: '2026-11-15',
    autoCreate: false,
    autoResolve: true,
  },
  {
    sport: 'soccer',
    competition: 'NWSL 2027',
    competitionId: 'nwsl-2027',
    phase: 'Season',
    startDate: '2027-03-01',
    endDate: '2027-11-14',
    autoCreate: false,
    autoResolve: false,
    notes: 'Enable auto-create once API provider confirmed',
  },

  // ── MLB ──────────────────────────────────────────────────────────────────────
  {
    sport: 'mlb',
    competition: 'MLB 2026',
    competitionId: 'mlb-2026',
    phase: 'Playoffs',
    startDate: '2026-09-29',
    endDate: '2026-10-24',
    autoCreate: true,
    autoResolve: true,
  },
  {
    sport: 'mlb',
    competition: 'MLB 2026',
    competitionId: 'mlb-2026',
    phase: 'World Series',
    startDate: '2026-10-25',
    endDate: '2026-10-31',
    autoCreate: true,
    autoResolve: true,
  },
  {
    sport: 'mlb',
    competition: 'MLB 2027',
    competitionId: 'mlb-2027',
    phase: 'Regular Season',
    startDate: '2027-04-01',
    endDate: '2027-09-28',
    autoCreate: false,
    autoResolve: false,
    notes:
      '~162 games per team (~2430 total); rolling window essential. Enable once API confirmed.',
  },
  {
    sport: 'mlb',
    competition: 'MLB 2027',
    competitionId: 'mlb-2027',
    phase: 'Playoffs',
    startDate: '2027-09-30',
    endDate: '2027-10-25',
    autoCreate: false,
    autoResolve: false,
  },

  // ── NBA ──────────────────────────────────────────────────────────────────────
  {
    sport: 'nba',
    competition: 'NBA 2026–27',
    competitionId: 'nba-regular-2026-27',
    phase: 'Regular Season',
    startDate: '2026-10-21',
    endDate: '2027-04-12',
    // Off until the launch leagues have run cleanly: about 1,230 games, each
    // with a 1,000-mana ante and a seeding cost on lopsided lines. Switch on
    // by 7 Oct to have opening night (21 Oct) created two weeks out.
    autoCreate: false,
    autoResolve: true,
    notes:
      'Resolution-only live mode: final score triggers auto-resolve. Mid-game odds move via human traders only — no automated probability nudging during play.',
  },
  {
    sport: 'nba',
    competition: 'NBA Playoffs 2027',
    competitionId: 'nba-playoffs-2027',
    phase: 'Playoffs',
    startDate: '2027-04-18',
    endDate: '2027-06-15',
    autoCreate: true,
    autoResolve: true,
    notes: 'Resolution-only live mode',
  },

  // ── NHL ──────────────────────────────────────────────────────────────────────
  // Games can't end level: overtime, then a shootout in the regular season.
  {
    sport: 'nhl',
    competition: 'NHL 2026–27',
    competitionId: 'nhl-2026-27',
    phase: 'Regular Season',
    startDate: '2026-10-06',
    endDate: '2027-04-15',
    autoCreate: false,
    autoResolve: true,
    notes: '1,312 games, up to 15 a night',
  },
  {
    sport: 'nhl',
    competition: 'NHL 2026–27',
    competitionId: 'nhl-2026-27',
    phase: 'Playoffs',
    startDate: '2027-04-17',
    endDate: '2027-06-20',
    autoCreate: false,
    autoResolve: true,
  },

  // ── College basketball ───────────────────────────────────────────────────────
  // The provider includes all college tournaments on the same feed.
  {
    sport: 'cbb',
    competition: 'NCAA Basketball 2026–27',
    competitionId: 'ncaab-season-2026-27',
    phase: 'Regular Season',
    startDate: '2026-11-01',
    endDate: '2027-03-14',
    autoCreate: false,
    autoResolve: true,
    notes:
      'Priority 1. Includes conference tournaments. High market volume: check creator funding before enabling.',
  },
  {
    sport: 'cbb',
    competition: 'NCAA Tournament 2027',
    competitionId: 'ncaab-tournament-2027',
    phase: 'March Madness',
    startDate: '2027-03-16',
    endDate: '2027-04-05',
    autoCreate: false,
    autoResolve: true,
    // The provider files every college game under one key, so the NIT and
    // other post-season tournaments fall in this window too.
    notes:
      '67 games. The NIT shares the window: check the dry run before switching on',
  },

  // ── WNBA ─────────────────────────────────────────────────────────────────────
  {
    sport: 'wnba',
    competition: 'WNBA 2026',
    competitionId: 'wnba-2026',
    phase: 'Playoffs',
    startDate: '2026-09-17',
    endDate: '2026-10-20',
    autoCreate: true,
    autoResolve: true,
    notes: 'Resolution-only live mode',
  },
  {
    sport: 'wnba',
    competition: 'WNBA 2027',
    competitionId: 'wnba-2027',
    phase: 'Season',
    startDate: '2027-05-01',
    endDate: '2027-10-19',
    autoCreate: false,
    autoResolve: false,
    notes: 'Enable auto-create once API confirmed',
  },
]

export const SPORTS_CALENDAR: SportsCalendarEntry[] = [
  ...ENTRIES.map((e) => ({
    ...e,
    oddsKey: ODDS_KEY_BY_COMPETITION[e.competitionId],
  })),
  ...additionalFeedCalendar(),
]

export function calendarStatus(
  entry: Pick<SportsCalendarEntry, 'startDate' | 'endDate'>,
  now = Date.now()
): SportsCalendarStatus {
  const start = new Date(`${entry.startDate}T00:00:00Z`).getTime()
  const end = new Date(`${entry.endDate}T23:59:59Z`).getTime()
  if (end < now) return 'completed'
  if (start <= now) return 'active'
  return 'upcoming'
}

/** Entries whose window contains `now`. */
export function activeCalendarEntries(now = Date.now()): SportsCalendarEntry[] {
  return SPORTS_CALENDAR.filter((e) => calendarStatus(e, now) === 'active')
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * A phase's window in ms. Dates are calendar days for a competition played
 * mostly in the US, so the end gets a day of slack: a Monday-night game at
 * 8pm ET is already the next day in UTC.
 */
export function phaseWindow(
  entry: Pick<SportsCalendarEntry, 'startDate' | 'endDate'>
): { from: number; to: number } {
  return {
    from: new Date(`${entry.startDate}T00:00:00Z`).getTime(),
    to: new Date(`${entry.endDate}T23:59:59Z`).getTime() + DAY_MS,
  }
}

/** Entries whose window overlaps [from, to]. */
export function calendarEntriesOverlapping(
  from: number,
  to: number
): SportsCalendarEntry[] {
  return SPORTS_CALENDAR.filter((e) => {
    const w = phaseWindow(e)
    return w.from <= to && w.to >= from
  })
}

/** All phases of one competition. */
export function calendarEntriesFor(
  competitionId: string
): SportsCalendarEntry[] {
  return SPORTS_CALENDAR.filter((e) => e.competitionId === competitionId)
}

/**
 * Other phases on the same Odds API feed whose dates overlap this one's.
 * The feed can't tell their games apart, so a game inside both is created
 * only if both are on (the CFP window overlaps the bowls'; a phase's last
 * US evening is the next phase's first UTC day).
 */
export function overlappingPhases(
  entry: SportsCalendarEntry
): SportsCalendarEntry[] {
  if (!entry.oddsKey) return []
  const w = phaseWindow(entry)
  return SPORTS_CALENDAR.filter((e) => {
    if (e === entry || e.oddsKey !== entry.oddsKey) return false
    const o = phaseWindow(e)
    return o.from <= w.to && w.from <= o.to
  })
}

// ─── Scheduler switches ───────────────────────────────────────────────────────
//
// Admins switch a phase's scheduled creation on or off from /admin/sports.
// A phase without a switch falls back to its `autoCreate` default.

/** A phase's key in the switches table and in `CompetitionSwitches`. */
export const calendarPhaseKey = (
  entry: Pick<SportsCalendarEntry, 'competitionId' | 'phase'>
) => `${entry.competitionId}:${entry.phase}`

/** Admin switches by `calendarPhaseKey`. */
export type CompetitionSwitches = Record<string, boolean>

/**
 * Whether phases without a switch run on their `autoCreate` default. Not on
 * dev: its scheduler creates nothing, and spends no credits, unless a phase
 * is switched on there.
 */
export const CALENDAR_DEFAULTS_ON = ENV === 'PROD'

/** Whether the scheduler creates games in this phase. */
export function autoCreates(
  entry: SportsCalendarEntry,
  switches: CompetitionSwitches
): boolean {
  if (entry.locked || !(entry.oddsKey || entry.provider)) return false
  return (
    switches[calendarPhaseKey(entry)] ??
    (CALENDAR_DEFAULTS_ON && entry.autoCreate)
  )
}

/** Why a phase's switch can't be set, or undefined when it can. */
export function competitionSwitchError(
  competitionId: string,
  phase: string,
  on: boolean
): string | undefined {
  const entry = SPORTS_CALENDAR.find(
    (e) => e.competitionId === competitionId && e.phase === phase
  )
  if (!entry) return `No calendar phase ${competitionId} / ${phase}`
  if (!entry.oddsKey && !entry.provider)
    return `${entry.competition} has no Odds API sport key or other feed`
  if (on && entry.locked) return entry.locked
  return undefined
}

/** A phase as the switches panel on /admin/sports shows it. */
export interface CompetitionSwitchState {
  competitionId: string
  competition: string
  phase: string
  sport: SportId
  startDate: string
  endDate: string
  status: SportsCalendarStatus
  /** Whether the scheduler creates games in this phase. */
  on: boolean
  /** What `on` is without a switch. */
  defaultOn: boolean
  locked?: string
  notes?: string
  /** Phases on the same feed whose dates overlap this one's, as labels. */
  overlaps: string[]
  /** Who set the switch and when; absent while the phase runs on its default. */
  updatedBy?: string
  updatedTime?: number
}

/** What goes in `sportsLeague`: the label the dashboards and the sport rail read. */
export const SPORT_LEAGUE_LABEL: Record<SportId, string> = {
  nfl: 'NFL',
  cfb: 'College Football',
  mlb: 'MLB',
  nba: 'NBA',
  wnba: 'WNBA',
  soccer: 'Soccer',
  f1: 'Formula 1',
  tdf: 'Tour de France',
  nhl: 'NHL',
  cbb: 'NCAAB',
  football: 'Football',
  basketball: 'Basketball',
  wcbb: 'WNCAAB',
  hockey: 'Ice Hockey',
  afl: 'AFL',
  nrl: 'NRL',
  handball: 'Handball',
  tennis: 'Tennis',
}

/** Which chip on /sports a calendar sport belongs to. */
export const SPORT_ID_TO_SPORT_KEY: Record<SportId, SportKey> = {
  nfl: 'nfl',
  cfb: 'ncaaf',
  mlb: 'mlb',
  nba: 'nba',
  wnba: 'wnba',
  soccer: 'soccer',
  f1: 'f1',
  tdf: 'other',
  nhl: 'nhl',
  cbb: 'ncaab',
  football: 'football',
  basketball: 'basketball',
  wcbb: 'wncaab',
  hockey: 'hockey',
  afl: 'afl',
  nrl: 'nrl',
  handball: 'handball',
  tennis: 'tennis',
}

// Hours after the start before the market closes: the longest realistic game
// plus overtime for each sport.
export const CLOSE_BUFFER_HOURS: Record<SportId, number> = {
  nfl: 4,
  cfb: 4,
  mlb: 4,
  nba: 3,
  wnba: 3,
  soccer: 2.5,
  f1: 2,
  tdf: 1,
  nhl: 4,
  cbb: 3,
  football: 4,
  basketball: 3,
  wcbb: 3,
  hockey: 4,
  afl: 4,
  nrl: 3,
  handball: 3,
  tennis: 6,
}

// ─── Readiness filters ────────────────────────────────────────────────────────
//
// The calendar now lists every feed the provider scores, most of them locked
// until their settlement rule is checked, so /admin/sports needs to show only
// the phases an admin can act on.

/**
 * 'on': the scheduler creates this phase's games.
 * 'ready': it would with a switch.
 * 'needs-validation': locked until its settlement rule is implemented or
 *   checked against provider results (knockout extra time, tennis walkovers).
 */
export type SwitchReadiness = 'on' | 'ready' | 'needs-validation'

export const switchReadiness = (
  s: Pick<CompetitionSwitchState, 'on' | 'locked'>
): SwitchReadiness => (s.locked ? 'needs-validation' : s.on ? 'on' : 'ready')

export interface SwitchFilter {
  /** 'ready' shows on and ready phases: everything that can run. */
  readiness: SwitchReadiness | 'all'
  sport: SportId | 'all'
  /** Matched against the competition, phase, notes and lock reason. */
  query: string
  showFinished: boolean
}

export const DEFAULT_SWITCH_FILTER: SwitchFilter = {
  readiness: 'ready',
  sport: 'all',
  query: '',
  showFinished: false,
}

const matchesReadiness = (
  s: Pick<CompetitionSwitchState, 'on' | 'locked'>,
  readiness: SwitchFilter['readiness']
) => {
  const r = switchReadiness(s)
  if (readiness === 'all') return true
  if (readiness === 'ready') return r !== 'needs-validation'
  return r === readiness
}

const matchesQuery = (s: CompetitionSwitchState, query: string) => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [
    s.competition,
    s.phase,
    s.notes ?? '',
    s.locked ?? '',
    SPORT_LEAGUE_LABEL[s.sport],
  ].some((text) => text.toLowerCase().includes(q))
}

/** Switches matching every part of the filter. */
export function filterSwitches(
  switches: CompetitionSwitchState[],
  filter: SwitchFilter
): CompetitionSwitchState[] {
  return switches.filter(
    (s) =>
      (filter.showFinished || s.status !== 'completed') &&
      (filter.sport === 'all' || s.sport === filter.sport) &&
      matchesReadiness(s, filter.readiness) &&
      matchesQuery(s, filter.query)
  )
}

/**
 * How many phases each readiness choice would show, given the rest of the
 * filter, so the choice labels say what they hide.
 */
export function countByReadiness(
  switches: CompetitionSwitchState[],
  filter: Omit<SwitchFilter, 'readiness'>
): Record<SwitchFilter['readiness'], number> {
  const rest = filterSwitches(switches, { ...filter, readiness: 'all' })
  const count = (readiness: SwitchFilter['readiness']) =>
    rest.filter((s) => matchesReadiness(s, readiness)).length
  return {
    all: rest.length,
    on: count('on'),
    ready: count('ready'),
    'needs-validation': count('needs-validation'),
  }
}
